/* eslint-disable no-plusplus */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable no-underscore-dangle */
/* eslint-disable react/no-this-in-sfc */
import { fabric } from 'fabric';

const RECTANGLE_CONTROLS = { ...fabric.Object.prototype.controls };

function getObjectSizeWithStroke(object) {
  const stroke = new fabric.Point(
    object.strokeUniform ? 1 / (object.scaleX || 1) : 1,
    object.strokeUniform ? 1 / (object.scaleY || 1) : 1,
  ).multiply(object.strokeWidth || 0);
  return new fabric.Point(object.width + stroke.x, object.height + stroke.y);
}

function polygonPositionHandler(dim, finalMatrix, fabricObject) {
  const x = fabricObject.points[this.pointIndex].x - fabricObject.pathOffset.x;
  const y = fabricObject.points[this.pointIndex].y - fabricObject.pathOffset.y;
  return fabric.util.transformPoint(
    { x, y },
    fabric.util.multiplyTransformMatrices(
      fabricObject.canvas.viewportTransform,
      fabricObject.calcTransformMatrix(),
    ),
  );
}

function actionHandler(eventData, transform, x, y) {
  const polygon = transform.target;
  const currentControl = polygon.controls[polygon.__corner];
  const mouseLocalPosition = polygon.toLocalPoint(new fabric.Point(x, y), 'center', 'center');

  const polygonBaseSize = getObjectSizeWithStroke(polygon);
  const size = polygon._getTransformedDimensions(0, 0);

  const finalPointPosition = {
    x: (mouseLocalPosition.x * polygonBaseSize.x) / size.x + polygon.pathOffset.x,
    y: (mouseLocalPosition.y * polygonBaseSize.y) / size.y + polygon.pathOffset.y,
  };

  if (polygon.snapToGrid && polygon.gridStep) {
    finalPointPosition.x = Math.round(finalPointPosition.x / polygon.gridStep) * polygon.gridStep;
    finalPointPosition.y = Math.round(finalPointPosition.y / polygon.gridStep) * polygon.gridStep;
  }

  polygon.points[currentControl.pointIndex] = finalPointPosition;
  polygon.set({ dirty: true });
  polygon.setCoords();
  polygon.canvas.requestRenderAll();

  return true;
}

function anchorWrapper(anchorIndex, fn) {
  return function (eventData, transform, x, y) {
    const fabricObject = transform.target;
    const absolutePoint = fabric.util.transformPoint(
      {
        x: fabricObject.points[anchorIndex].x - fabricObject.pathOffset.x,
        y: fabricObject.points[anchorIndex].y - fabricObject.pathOffset.y,
      },
      fabricObject.calcTransformMatrix(),
    );

    const actionPerformed = fn(eventData, transform, x, y);

    const polygonBaseSize = getObjectSizeWithStroke(fabricObject);
    const newX = (fabricObject.points[anchorIndex].x - fabricObject.pathOffset.x)
      / polygonBaseSize.x;
    const newY = (fabricObject.points[anchorIndex].y - fabricObject.pathOffset.y)
      / polygonBaseSize.y;

    fabricObject.setPositionByOrigin(absolutePoint, newX + 0.5, newY + 0.5);
    return actionPerformed;
  };
}

const isRectangleShape = (shape) => {
  // If it's already a fabric.Rect, it's definitely a rectangle
  if (shape.type === 'rect') return true;

  // If it's a polygon, check if it's a rectangle
  if (shape.type === 'polygon') {
    const polygon = shape;
    if (!polygon.points || polygon.points.length !== 4) return false;

    // Get the points
    const points = polygon.points.map((p) => ({ x: p.x, y: p.y }));

    // Calculate distances between consecutive points
    const distances = [];
    for (let i = 0; i < 4; i++) {
      const p1 = points[i];
      const p2 = points[(i + 1) % 4];
      // eslint-disable-next-line no-restricted-properties
      const dist = Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
      distances.push(dist);
    }

    // Check if opposite sides are equal (with small tolerance)
    const tolerance = 0.1;
    const oppositeSidesEqual = Math.abs(distances[0] - distances[2]) < tolerance
      && Math.abs(distances[1] - distances[3]) < tolerance;

    if (!oppositeSidesEqual) return false;

    // Check if angles are approximately 90 degrees
    for (let i = 0; i < 4; i++) {
      const p1 = points[i];
      const p2 = points[(i + 1) % 4];
      const p3 = points[(i + 2) % 4];

      const v1 = { x: p2.x - p1.x, y: p2.y - p1.y };
      const v2 = { x: p3.x - p2.x, y: p3.y - p2.y };

      const dotProduct = v1.x * v2.x + v1.y * v2.y;
      const mag1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
      const mag2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);

      const cosAngle = dotProduct / (mag1 * mag2);

      // cos(90°) = 0, allow small tolerance
      if (Math.abs(cosAngle) > 0.1) return false;
    }

    return true;
  }

  return false;
};

const getPolygonEditingControls = (polygon) => {
  const controls = {};
  for (let i = 0; i < polygon.points.length; i++) {
    controls[`p${i}`] = new fabric.Control({
      pointIndex: i,
      positionHandler: polygonPositionHandler,
      actionHandler,
      actionName: 'modifyPolygon',
      cursorStyle: 'crosshair',
    });
  }
  return controls;
};

function setShapeEditingMode(object, isRectangle) {
  if (isRectangle) {
    // --- RECTANGLE EDITING MODE ---

    // 1. Switch to standard bounding box controls
    // eslint-disable-next-line no-param-reassign
    object.controls = RECTANGLE_CONTROLS;

    // 2. Ensure all standard controls are visible
    object.setControlsVisibility({
      tl: true,
      tr: true,
      bl: true,
      br: true,
      ml: true,
      mr: true,
      mt: true,
      mb: true,
      mtr: true,
    });

    // 3. Re-enable object-level scaling/rotation
    object.set({
      lockScalingX: false,
      lockScalingY: false,
      lockRotation: false,
      lockSkewingX: false,
      lockSkewingY: false,
    });
  } else {
    // --- POLYGON POINT-EDITING MODE ---

    // 1. Switch to custom point-editing controls
    // eslint-disable-next-line no-param-reassign
    object.controls = getPolygonEditingControls(object);

    // 2. Hide standard bounding box controls
    object.setControlsVisibility({
      tl: false,
      tr: false,
      bl: false,
      br: false,
      ml: false,
      mr: false,
      mt: false,
      mb: false,
      mtr: false,
    });

    // 3. Disable object-level scaling/rotation in point-editing mode
    object.set({
      lockScalingX: true,
      lockScalingY: true,
      lockRotation: true,
    });
  }

  // Recalculate coordinates and render the canvas to apply new controls
  object.setCoords();
  if (object.canvas) {
    object.canvas.requestRenderAll();
  }
}

export {
  polygonPositionHandler,
  actionHandler,
  anchorWrapper,
  isRectangleShape,
  getPolygonEditingControls,
  setShapeEditingMode,
};
